/**
 * Add Supply Configuration
 * Type-specific configuration for adding supply inventory
 */

import { supabase } from '../../../../../supabaseClient';

export const addSupplyConfig = {
  type: 'supplies',
  displayName: 'Supply',
  tableName: 'supplies',
  inventoryTable: 'supply_inventory',
  itemsTable: 'supply_inventory_items',
  transactionsTable: 'supply_transactions',
  itemIdField: 'supply_id',
  
  // Storage buckets
  storageBuckets: {
    invoices: 'supplies-transactions-invoices',
    po: 'supplies-transactions-po',
    coa: 'supplies-transactions-coa',
    msds: 'supplies-transactions-msds',
    bol: 'supplies-transactions-bol'
  },
  
  // Form fields configuration
  formFields: [
    { name: 'supply_id', label: 'Supply', type: 'select', required: true, section: 'basic' },
    { name: 'location_id', label: 'Location', type: 'select', required: true, section: 'basic' },
    { name: 'quantity', label: 'Quantity', type: 'number', required: true, min: 1, section: 'basic' },
    { name: 'lot_number', label: 'Lot Number', type: 'text', required: true, section: 'lot' },
    { name: 'batch_number', label: 'Batch Number', type: 'text', required: true, section: 'lot' },
    { name: 'supplier_id', label: 'Vendor', type: 'select', required: false, section: 'vendor' },
    { name: 'po_number', label: 'PO Number', type: 'text', required: false, section: 'vendor' },
    { name: 'po_date', label: 'PO Date', type: 'date', required: false, section: 'vendor' },
    { name: 'invoice_number', label: 'Invoice Number', type: 'text', required: false, section: 'vendor' },
    { name: 'invoice_date', label: 'Invoice Date', type: 'date', required: false, section: 'vendor' },
    { name: 'invoice_document', label: 'Invoice Document', type: 'file', required: false, accept: '.pdf,.jpg,.jpeg,.png', section: 'documents' },
    { name: 'po_document', label: 'PO Document', type: 'file', required: false, accept: '.pdf,.jpg,.jpeg,.png', section: 'documents' },
    { name: 'coa_document', label: 'COA Document', type: 'file', required: false, accept: '.pdf,.jpg,.jpeg,.png', section: 'documents' },
    { name: 'msds_document', label: 'MSDS Document', type: 'file', required: false, accept: '.pdf,.jpg,.jpeg,.png', section: 'documents' },
    { name: 'bol_document', label: 'BOL Document', type: 'file', required: false, accept: '.pdf,.jpg,.jpeg,.png', section: 'documents' },
    { name: 'notes', label: 'Notes', type: 'textarea', required: false, rows: 3, section: 'additional' }
  ],
  
  validationRules: {
    quantity: (value) => {
      const num = parseInt(value);
      if (isNaN(num) || num <= 0) return 'Quantity must be a positive number';
      return null;
    }
  },
  
  submitHandler: async (formData, companyId) => {
    try {
      // CRITICAL: Purge ANY existing records (active OR inactive) with same lot/batch
      // This prevents database triggers from reactivating old deleted records
      const { data: existingItems } = await supabase
        .from('supply_inventory_items')
        .select('id, source_transaction_id')
        .eq('company_id', companyId)
        .eq('supply_id', formData.supply_id)
        .eq('location_id', formData.location_id)
        .eq('lot_number', formData.lot_number)
        .eq('batch_number', formData.batch_number);
      
      if (existingItems && existingItems.length > 0) {
        // Delete source transactions first
        const txIds = existingItems.map(i => i.source_transaction_id).filter(id => id);
        if (txIds.length > 0) {
          await supabase
            .from('supply_transactions')
            .delete()
            .in('id', txIds);
        }
        
        // Delete inventory items
        await supabase
          .from('supply_inventory_items')
          .delete()
          .eq('company_id', companyId)
          .eq('supply_id', formData.supply_id)
          .eq('location_id', formData.location_id)
          .eq('lot_number', formData.lot_number)
          .eq('batch_number', formData.batch_number);
      }
      
      // Fetch unit_cost from supplies table
      const { data: supplyData, error: supplyError } = await supabase
        .from('supplies')
        .select('unit_cost')
        .eq('id', formData.supply_id)
        .single();
      
      if (supplyError) throw supplyError;
      
      // Upload documents FIRST (before creating transaction)
      const documentPaths = {};
      const bucketMap = {
        invoice: 'supplies-transactions-invoices',
        po: 'supplies-transactions-po',
        coa: 'supplies-transactions-coa',
        msds: 'supplies-transactions-msds',
        bol: 'supplies-transactions-bol'
      };
      
      for (const docType of ['invoice', 'po', 'coa', 'msds', 'bol']) {
        const file = formData[`${docType}_document`];
        if (file) {
          const fileName = `${companyId}/${Date.now()}_${file.name}`;
          const bucketName = bucketMap[docType];
          const { error: uploadError } = await supabase.storage.from(bucketName).upload(fileName, file);
          if (uploadError) throw uploadError;
          documentPaths[`${docType}_file_path`] = fileName;
        }
      }
      
      // Create transaction record with document paths
      const transactionData = {
        company_id: companyId,
        supply_id: formData.supply_id,
        location_id: formData.location_id,
        transaction_type: 'inventory_creation',
        quantity: parseInt(formData.quantity),
        lot_number: formData.lot_number,
        batch_number: formData.batch_number,
        supplier_id: formData.supplier_id || null,
        po_number: formData.po_number || null,
        po_date: formData.po_date || null,
        invoice_number: formData.invoice_number || null,
        invoice_date: formData.invoice_date || null,
        unit_cost: supplyData?.unit_cost || null,
        notes: formData.notes || null,
        ...documentPaths,
        created_at: new Date().toISOString()
      };
      
      const { data: transaction, error: txError } = await supabase
        .from('supply_transactions')
        .insert(transactionData)
        .select()
        .single();
      
      if (txError) throw txError;
      
      // Create inventory item (NO document paths here!)
      const inventoryItemData = {
        company_id: companyId,
        supply_id: formData.supply_id,
        location_id: formData.location_id,
        current_quantity: parseInt(formData.quantity),
        original_quantity: parseInt(formData.quantity),
        lot_number: formData.lot_number,
        batch_number: formData.batch_number,
        source_transaction_id: transaction.id,
        is_active: true,
        created_at: new Date().toISOString()
      };
      
      const { data: inventoryItem, error: itemError } = await supabase
        .from('supply_inventory_items') 
        .insert(inventoryItemData)
        .select()
        .single();
      
      if (itemError) throw itemError;
      
      await supabase.rpc('recalculate_supply_aggregate_inventory', {
        p_company_id: companyId,
        p_supply_id: formData.supply_id,
        p_location_id: formData.location_id
      });
      
      return { success: true, transaction, inventoryItem };
    } catch (error) {
      console.error('Error adding supply inventory:', error);
      throw error;
    }
  }
};

export default addSupplyConfig;
